import { computedFrom, Disposable } from "aurelia-binding";
import { autoinject } from "aurelia-dependency-injection";
import { EventAggregator } from "aurelia-event-aggregator";
import { PLATFORM } from "aurelia-pal";
import { Router, RouterConfiguration } from "aurelia-router";
import * as moment from "moment";
import { Moment } from "moment";
import { Key } from "ts-keycode-enum";
import Infrastructure from "../../common/infrastructure";
import nameof from "../../common/nameof";
import { OidcConfiguration } from "../../common/oidc/oidc-configuration";
import { KeyListener, KeyPressPublish } from "../../common/utilities/key-listener";
import { IKeyEventPayload } from "../../interfaces/i-keyevent-payload";
import { accountIdKey, AuthService } from "../../services/auth-service";
import { HttpService } from "../../services/http-service";
import { UsersService } from "./../../services/users-service";
import { AppRoutes } from "./app-routes";
import "./app.scss";
import { AuthorizeStep } from "./authorize-step";

@autoinject
export class App {
    private _usersService: UsersService;
    private _eaSubscriptions: Disposable[] = [];
    private _httpService: HttpService;
    private _ea: EventAggregator;
    private _authService: AuthService;
    private _oidcConfiguration: OidcConfiguration;
    private _keyListener: KeyListener;
    private _appRoutes: AppRoutes;
    public router: Router;
    public currentYear: string = moment().format("YYYY");
    public releaseVersion: string = Infrastructure.releaseVersion;
    public releaseTimestamp: Moment = moment(new Date(Infrastructure.releaseTimestamp));
    public releaseEnvironment: string = Infrastructure.releaseEnvironment;
    public axxessCentralUrl: string = Infrastructure.axxessCentral;
    public isAccountLoading: boolean = false;

    @computedFrom(nameof<App>("releaseTimestamp"))
    public get releasedOn() {
        if (this.releaseTimestamp.isValid()) {
            return `Released on ${this.releaseTimestamp.format("MM/DD/YYYY hh:mm:ss a")}`;
        } else {
            return "";
        }
    }

    public constructor(
        httpService: HttpService,
        ea: EventAggregator,
        authService: AuthService,
        oidcConfiguration: OidcConfiguration,
        keyListener: KeyListener,
        appRoutes: AppRoutes,
        usersService: UsersService
    ) {
        this._httpService = httpService;
        this._ea = ea;
        this._authService = authService;
        this.initSubscriptions();
        this._httpService.configureFetch();
        this._oidcConfiguration = oidcConfiguration;
        this._keyListener = keyListener;
        this._appRoutes = appRoutes;
        this._usersService = usersService;
    }

    private initSubscriptions() {
        window.addEventListener("storage", async (event: StorageEvent) => {
            console.log("Storage event triggered", event);
            let isUserLoggedIn = await this._authService.isLoggedIn();
            if (isUserLoggedIn && event.key === accountIdKey && event.newValue !== event.oldValue) {
                console.log("Account has been changed in another tab, Reloading");
                window.location.reload();
            }
        });
    }

    public async attached(): Promise<void> {
        this._keyListener.attach();
        this._eaSubscriptions.push(this._ea.subscribe(KeyPressPublish, this.keyListenHandler.bind(this)));
    }

    public async configureRouter(routerConfig: RouterConfiguration, router: Router) {
        this.router = router;
        routerConfig.title = "AxxessHospice";
        routerConfig.options.pushState = true;
        let appRoutes = await this._appRoutes.getRoutes();
        routerConfig.map(appRoutes);
        routerConfig.mapUnknownRoutes(PLATFORM.moduleName("./not-found"));
        routerConfig.fallbackRoute("/");
        this._oidcConfiguration.configureRouter(routerConfig, router);
        routerConfig.addAuthorizeStep(AuthorizeStep);
    }

    private keyListenHandler(payload: IKeyEventPayload) {
        let event = payload.KeyboardEvent;
        let isCtrl = event.ctrlKey;
        let isAlt = event.altKey;
        let isKeyDPressed = event.keyCode === Key.D;
        let isKeyTPressed = event.keyCode === Key.T;
        if (isCtrl && isAlt && isKeyDPressed) {
            this.initiateNavigationToDataLoad();
        } else if (isCtrl && isAlt && isKeyTPressed) {
            this.initiateNavigationToDataFix();
        }
    }

    private async initiateNavigationToDataLoad() {
        let isProdEnvironment = Infrastructure.isProdEnvironment;
        let isUserAuthorized = true;
        if (isProdEnvironment) {
            isUserAuthorized = await this._usersService.isTemporaryUser();
        }
        if (isUserAuthorized) {
            this.router.navigate("/admin/data-load");
        }
    }

    private async initiateNavigationToDataFix() {
        let isProdEnvironment = Infrastructure.isProdEnvironment;
        let isUserAuthorized = true;
        if (isProdEnvironment) {
            isUserAuthorized = await this._usersService.isTemporaryUser();
        }
        if (isUserAuthorized) {
            this.router.navigate("/admin/data-fix");
        }
    }

    public detached() {
        if (this._eaSubscriptions.length > 0) {
            this._eaSubscriptions.forEach((sub) => sub.dispose());
        }
        this._keyListener.detach();
    }
}
